import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Button,
    Container,
    Typography,
    Divider,
    TextField,
    Menu,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Autocomplete,
    Chip,
    InputAdornment,
    CircularProgress,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon } from "@mui/icons-material";

import DataZonesTable from "./DataZonesTable";
import { useAuth } from "../../../helpers/AuthProvider";
import queryGraphql from "../../../helpers/ApiInterface";
import MainDrawer from "../../MainDrawer";
import MiniBot from "../../MiniBot";

interface SolutionOwnerInfo {
    id: string;
    name: string;
    email?: string;
    googleProfilePicture?: string | null;
}

interface ParentSolutionInfo {
    id: string;
    name: string;
}

interface DataZone {
    id: string;
    zoneName: string;
    lakeName: string;
    projectId: string;
    location: string;
    type: string;
    status: string;
    solutionCategoryType: string;
    cloudProvider: string;
    updatedAt: string;
    createdAt: string;
    solutionOwner?: SolutionOwnerInfo | null;
    parentSolution?: ParentSolutionInfo | null;
    reasonForRejection?: string;
    solutionType?: string;
}

interface SearchOptions {
    chipLabel: string;
    dropdownLabel: string;
    category: keyof DataZone | string;
    groupBy: string;
}

let searchOptionsMasterList: SearchOptions[] = [];

const DataZones: React.FC = () => {
    const [allDataZones, setAllDataZones] = useState<DataZone[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const { user } = useAuth();
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState<SearchOptions[]>([]);
    const [columnFilterAnchorEl, setColumnFilterAnchorEl] = useState<null | HTMLElement>(null);

    const initialColumnFilters: Record<string, boolean> = {
        zoneName: true,
        lakeName: true,
        type: true,
        cloudProvider: true,
        location: true,
        solutionOwner: true,
        createdAt: true,
    };
    const [columnFilters, setColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);
    const [tempColumnFilters, setTempColumnFilters] = useState<Record<string, boolean>>(initialColumnFilters);

    useEffect(() => {
        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await queryGraphql(`
                    query GetDataplexZones {
                      getDataplexZones {
                        id
                        name
                        solutionType
                        solutionCategoryType
                        status
                        details {
                          dataplexZone {
                            projectId
                            location
                            type
                          }
                        }
                        reasonForRejection
                        createdAt
                        updatedAt
                        solutionOwner {
                          id
                          name
                          email
                          googleProfilePicture
                        }
                        parentSolution {
                          id
                          name
                        }
                        cloudProvider
                      }
                    }
                `);

                const dataZonesData = response.data?.getDataplexZones || response.getDataplexZones || [];

                if (dataZonesData) {
                    const processedDataZones = dataZonesData.map((solution: any): DataZone => ({
                        id: solution.id,
                        projectId: solution.details?.dataplexZone?.projectId || "N/A",
                        zoneName: solution.name || "Unknown Data Zone",
                        lakeName: solution.parentSolution?.name || "N/A",
                        location: solution.details?.dataplexZone?.location || "N/A",
                        type: solution.details?.dataplexZone?.type || "N/A",
                        status: (solution.status || 'pending').toLowerCase(),
                        solutionCategoryType: solution.solutionCategoryType || "N/A",
                        cloudProvider: solution.cloudProvider || "N/A",
                        solutionOwner: solution.solutionOwner,
                        parentSolution: solution.parentSolution,
                        solutionType: solution.solutionType,
                        updatedAt: solution.updatedAt,
                        createdAt: solution.createdAt,
                        reasonForRejection: solution.reasonForRejection,
                    }));
                    setAllDataZones(processedDataZones);

                    searchOptionsMasterList = processedDataZones
                        .map((dz: DataZone) => [
                            { category: "zoneName", groupBy: "DATA ZONE NAME", chipLabel: `Zone: ${dz.zoneName}`, dropdownLabel: dz.zoneName },
                            { category: "lakeName", groupBy: "LAKE NAME", chipLabel: `Lake: ${dz.lakeName}`, dropdownLabel: dz.lakeName },
                            { category: "type", groupBy: "TYPE", chipLabel: `Type: ${dz.type}`, dropdownLabel: dz.type },
                            { category: "cloudProvider", groupBy: "CLOUD PROVIDER", chipLabel: `Provider: ${dz.cloudProvider}`, dropdownLabel: dz.cloudProvider },
                            { category: "location", groupBy: "LOCATION", chipLabel: `Location: ${dz.location}`, dropdownLabel: dz.location },
                            { category: "status", groupBy: "STATUS", chipLabel: `Status: ${dz.status}`, dropdownLabel: dz.status.charAt(0).toUpperCase() + dz.status.slice(1) },
                            ...(dz.solutionOwner ? [{ category: "solutionOwner.name" as any, groupBy: "RESOURCE OWNER", chipLabel: `Owner: ${dz.solutionOwner.name}`, dropdownLabel: dz.solutionOwner.name }] : []),
                        ])
                        .flat();

                    searchOptionsMasterList = searchOptionsMasterList.filter(
                        (obj, index, self) =>
                            obj.dropdownLabel &&
                            index === self.findIndex((t) =>
                                t.category === obj.category && t.dropdownLabel === obj.dropdownLabel
                            )
                    );
                    searchOptionsMasterList.sort((a, b) => {
                        const groupA = a.groupBy.toUpperCase();
                        const groupB = b.groupBy.toUpperCase();
                        if (groupA > groupB) return 1;
                        if (groupA < groupB) return -1;
                        const labelA = a.dropdownLabel.toUpperCase();
                        const labelB = b.dropdownLabel.toUpperCase();
                        if (labelA > labelB) return 1;
                        if (labelA < labelB) return -1;
                        return 0;
                    });
                }
            } catch (error) {
                console.error("Error fetching Data Zones:", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleColumnFilterClick = (event: React.MouseEvent<HTMLElement>) => {
        setTempColumnFilters(columnFilters);
        setColumnFilterAnchorEl(event.currentTarget);
    };

    const handleColumnFilterClose = () => {
        setColumnFilterAnchorEl(null);
    };

    const handleTempColumnFilterChange = (column: string) => {
        setTempColumnFilters((prevFilters) => ({
            ...prevFilters,
            [column]: !prevFilters[column],
        }));
    };

    const handleApplyColumnFilters = () => {
        setColumnFilters(tempColumnFilters);
        handleColumnFilterClose();
    };

    const getDisplayLabelForKey = (key: string): string => {
        if (key === 'solutionOwner') return 'Resource Owner';
        if (key === 'createdAt') return 'Added On';
        if (key === 'zoneName') return 'Data Zone Name';
        if (key === 'lakeName') return 'Lake Name';
        if (key === 'cloudProvider') return 'Cloud Provider';
        const result = key.replace(/([A-Z])/g, ' $1');
        return result.charAt(0).toUpperCase() + result.slice(1).trim();
    };


    const globallyFilteredDataZones =
        searchQuery.length === 0
            ? allDataZones
            : allDataZones.filter((dataZone: DataZone) => {
                return searchQuery.every(queryParam => {
                    let dataZoneValue: any;
                    if (queryParam.category === "solutionOwner.name") {
                        dataZoneValue = dataZone.solutionOwner?.name;
                    } else {
                        dataZoneValue = dataZone[queryParam.category as keyof DataZone];
                    }
                    if (dataZoneValue === null || dataZoneValue === undefined) return false;
                    return String(dataZoneValue).toLowerCase().includes(queryParam.dropdownLabel.toLowerCase());
                });
            });

    const pendingDataZones = globallyFilteredDataZones.filter(p => p.status === 'pending');
    const activeDataZones = globallyFilteredDataZones.filter(p => p.status === 'active' || p.status === 'approved');
    const rejectedDataZones = globallyFilteredDataZones.filter(p => p.status === 'rejected');

    const totalDataZonesOverall = allDataZones.length;
    const totalMatchingSearch = globallyFilteredDataZones.length;

    const canAddDataZone = true;

    const handleAddDataZone = () => {
        navigate("/datasolutions/data-zones/add");
    };

    if (isLoading) {
        return (
            <Box sx={{ display: "flex" }}>
                <MainDrawer index={"datasolutions-data-zones"} />
                <Box component="main" sx={{ flex: 1, p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ display: "flex" }}>
            <MainDrawer index={"datasolutions-data-zones"} />
            <Box
                component="main"
                sx={{
                    flex: 1,
                    bgcolor: "#fafafa",
                    minHeight: "100vh",
                }}
            >
                <MiniBot />
                <Container maxWidth={false} disableGutters>
                    <Box bgcolor="#FFFFFF" p={{xs:1.5, sm:2}} mb="18px">
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: "space-between",
                                alignItems: { xs: 'flex-start', sm: 'center' },
                                mx:2
                            }}
                        >
                            <Typography fontSize="22px" gutterBottom fontFamily="Gilroy-Bold" color="#1F2937">
                                Data Zones
                            </Typography>
                            <Box sx={{display:'flex', justifyContent:'center'}}>
                                <Box>
                            {canAddDataZone && (
                                <Button
                                    variant="contained"
                                    sx={{
                                        backgroundColor: "black", color: "white", padding: "8px 24px",
                                        fontSize: "0.875rem", textTransform: "none", fontFamily: "Gilroy-Bold",
                                         '&:hover': { backgroundColor: '#333333' }
                                    }}
                                    onClick={handleAddDataZone}
                                    disabled={!user.roles.includes("cluster_owner") && !user.roles.includes("viewer")}
                                >
                                    Add New Data Zone
                                </Button>
                                
                            )}
                            </Box>
                            <IconButton
                                                                  aria-label="Help" 
                                                                  onClick={()=> window.open(`${process.env.REACT_APP_HOST_DOMAIN}/docs` , "_blank")}
                                                                  sx={{
                                                                    padding: 0, 
                                                                    border: 'none',  
                                                                    ml:2,
                                                                    '&:hover': {
                                                                      
                                                                      backgroundColor: 'action.hover', 
                                                                    },
                                                                    
                                                                  }}
                                                                >
                                                                  <Box
                                                                    component="img"
                                                                    src="/assets/Help.svg" 
                                                                    alt="" 
                                                                    sx={{
                                                                      width: 40,  
                                                                      height: 40,
                                                                      display: 'block', 
                                                                    }}
                                                                  />
                                                                </IconButton>
                            </Box>
                        </Box>
                        <Divider sx={{ marginY: 1 }} />
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: 'wrap' }}>
                            <Autocomplete
                                multiple
                                options={searchOptionsMasterList}
                                filterSelectedOptions
                                getOptionLabel={(option) => option.dropdownLabel}
                                value={searchQuery}
                                groupBy={(option) => option.groupBy}
                                renderTags={(tagValue, getTagProps) =>
                                    tagValue.map((option, index) => (
                                        <Chip {...getTagProps({ index })} size="small" label={option.chipLabel} sx={{ fontFamily: "Gilroy-Regular" }} />
                                    ))
                                }
                                onChange={(e, newVal) => {
                                    setSearchQuery(newVal as SearchOptions[]);
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="filled"
                                        placeholder="Search by zone name, lake, type, etc."
                                        fullWidth
                                        size="small"
                                        hiddenLabel
                                        sx={{ minWidth: { xs: '100%', sm: 300, md: 500 }, flexGrow: 1 }}
                                        InputProps={{
                                            ...params.InputProps,
                                            startAdornment: (
                                                <>
                                                    <InputAdornment position="start" sx={{ pl: 0.5 }}>
                                                        <img src="/assets/search-normal.svg" alt="Search Icon" style={{ width: "16px" }} />
                                                    </InputAdornment>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ),
                                            disableUnderline: true,
                                            style: { backgroundColor: "#f4f4f4", minHeight: "44px", borderRadius: "8px", fontFamily: "Gilroy-Regular", fontSize: "14px" },
                                        }}
                                    />
                                )}
                                PaperComponent={(props) => <Paper elevation={3} {...props} />}
                            />
                            <Button
                                sx={{
                                
                                    border: "1px solid #bbb",
                                    borderRadius: "10px",
                                    height: "45px",
                                    width: "45px",
                                }}
                                onClick={handleColumnFilterClick}
                            >
                                <img src="/assets/pause.svg" alt="Filter Icon" style={{ width: "16px" }} />
                            </Button>
                            <Menu
                                anchorEl={columnFilterAnchorEl}
                                open={Boolean(columnFilterAnchorEl)}
                                onClose={handleColumnFilterClose}
                                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                                transformOrigin={{ vertical: "top", horizontal: "right" }}
                                PaperProps={{ sx: { border: '1px solid #E0E0E0', borderRadius: '8px', boxShadow: '0px 4px 12px rgba(0,0,0,0.1)', mt: 1 } }}
                            >
                                <Box sx={{ p: "16px", minWidth: 240, maxWidth: 280 }}>
                                    <Typography sx={{ fontFamily: "Gilroy-Bold", fontSize: "16px", fontWeight: 700, color: "#1F2937", marginBottom: "16px" }}>
                                        TABLE COLUMNS
                                    </Typography>
                                    <FormGroup>
                                        {Object.entries(tempColumnFilters).map(([key, value]) => (
                                            <FormControlLabel
                                                key={key}
                                                control={
                                                    <Checkbox
                                                        checked={value}
                                                        onChange={() => handleTempColumnFilterChange(key)}
                                                        size="small"
                                                        sx={{ color: "#4B5563", "&.Mui-checked": { color: "#000000" }, "& .MuiSvgIcon-root": { fontSize: "22px" }, py: '4px', mr: '8px' }}
                                                    />
                                                }
                                                label={<Typography sx={{ fontFamily: "Gilroy-Medium", fontSize: '17px', color: '#1F2937', lineHeight: '1.5', py: '1vh' }}>{getDisplayLabelForKey(key)}</Typography>}
                                                sx={{ marginBottom: '4px' }}
                                            />
                                        ))}
                                    </FormGroup>
                                    <Button
                                        variant="contained" fullWidth onClick={handleApplyColumnFilters}
                                        sx={{ fontFamily: "Gilroy-Bold", fontSize: "14px", fontWeight: 700, color: "#FFFFFF", backgroundColor: "#000000", textTransform: 'uppercase', borderRadius: "6px", padding: "10px 0", marginTop: "20px", '&:hover': { backgroundColor: "#333333" } }}
                                    >
                                        Apply
                                    </Button>
                                </Box>
                            </Menu>
                        </Box>
                    </Box>

                    <Box m={{xs: 1, sm:2}}>
                        {totalDataZonesOverall > 0 && totalMatchingSearch > 0 &&(
                            <>
                        <Accordion
                            defaultExpanded={pendingDataZones.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={pendingDataZones.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/clock.svg" alt="Pending Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Pending Data Zones ({pendingDataZones.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {pendingDataZones.length > 0 ? (
                                    <DataZonesTable
                                        dataZones={pendingDataZones}
                                        columnFilters={columnFilters}
                                        statusType="pending"
                                    />
                                ) : (
                                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No pending Data Zones match your filters.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={activeDataZones.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", mb: "16px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={activeDataZones.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/tickcircle.svg" alt="Active Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Active Data Zones ({activeDataZones.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {activeDataZones.length > 0 ? (
                                    <DataZonesTable
                                        dataZones={activeDataZones}
                                        columnFilters={columnFilters}
                                        statusType="active"
                                    />
                                ) : (
                                     <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No active Data Zones match your filters.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>

                        <Accordion
                            defaultExpanded={rejectedDataZones.length > 0}
                            elevation={0}
                            sx={{ borderRadius: "8px", "&:before": { display: "none" }, bgcolor: "#fafafa" }}
                            disabled={rejectedDataZones.length === 0 && searchQuery.length > 0}
                        >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                    <img src="/assets/reject.svg" alt="Rejected Icon" style={{ width: "20px", marginRight: "8px" }} />
                                    <Typography fontSize="16px" fontFamily="Gilroy-SemiBold">
                                        Rejected Data Zones ({rejectedDataZones.length})
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ p: { xs: 1, sm: 2 } }}>
                                {rejectedDataZones.length > 0 ? (
                                    <DataZonesTable
                                        dataZones={rejectedDataZones}
                                        columnFilters={columnFilters}
                                        statusType="rejected"
                                    />
                                ) : (
                                     <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontFamily:"Gilroy-Regular", py:2 }}>
                                        No rejected Data Zones match your filters.
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                        </>)}
                         {totalDataZonesOverall > 0 && totalMatchingSearch === 0 && !isLoading && (
                            <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" sx={{ textAlign: 'center', mt: 5, p: 2, backgroundColor:"#FFFFFF", borderRadius:"8px" }}>
                                <img src="/assets/inbox-icon.svg" alt="No Data Zones Match" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No Data Zones match your search
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '350px' }}>
                                    Try adjusting your search terms or filters.
                                </Typography>
                            </Box>
                        )}

                        {totalDataZonesOverall === 0 && !isLoading && (
                             <Box
                                display="flex" flexDirection="column" justifyContent="center" alignItems="center"
                                sx={{
                                    textAlign: 'center', mt: { xs: 2, sm: 5 }, px: 2, py: 4,
                                    backgroundColor: '#FFFFFF', borderRadius: "8px",
                                    boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
                                }}
                            >
                                <img src="/assets/inbox-icon.svg" alt="No Data Zones" style={{ width: '100px', marginBottom: '20px' }} />
                                <Typography variant="h6" sx={{ mb: 1, fontFamily: "Gilroy-SemiBold", color: "#1F2937" }}>
                                    No Data Zones are added yet
                                </Typography>
                                <Typography variant="body2" color="#6B7280" sx={{ mb: 3, fontFamily: "Gilroy-Regular", maxWidth: '380px' }}>
                                    Click the button above to add your first Data Zone.
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Container>
            </Box>
        </Box>
    );
};

export default DataZones;